import { fetchAuth } from "./api";

async function readJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function buildApiError(response, body, fallbackMessage) {
  const error = new Error(body?.error || body?.message || fallbackMessage);
  error.status = response.status;
  error.body = body;
  return error;
}

export async function fetchAlerts({ status = "all", limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({
    status,
    limit: String(limit),
    offset: String(offset),
  });

  const response = await fetchAuth(`/alerts?${params.toString()}`);
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to fetch alerts");
  }

  return {
    alerts: Array.isArray(data.alerts) ? data.alerts : [],
    meta: data.meta || {},
  };
}

export async function fetchUnreadAlertCount() {
  const response = await fetchAuth("/alerts/unread-count");
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to fetch unread alert count");
  }

  return {
    count: Number.isFinite(Number(data.unread_count)) ? Number(data.unread_count) : 0,
    maxSeverity: data.max_severity || null,
  };
}

export async function markAlertAsRead(alertId) {
  const response = await fetchAuth(`/alerts/${alertId}/read`, { method: "PATCH" });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to mark alert as read");
  }

  return data;
}

export async function markAlertAsUnread(alertId) {
  const response = await fetchAuth(`/alerts/${alertId}/unread`, { method: "PATCH" });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to mark alert as unread");
  }

  return data;
}

export async function deleteAlertForCurrentUser(alertId) {
  const response = await fetchAuth(`/alerts/${alertId}`, { method: "DELETE" });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to delete alert");
  }

  return data;
}

export async function fetchPlayerRiskAlert(playerId, options = {}) {
  if (!playerId) return null;

  const response = await fetchAuth(`/players/${playerId}/risk-alert`, options);
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to fetch player risk alert");
  }

  return data.alert || null;
}

export async function fetchPlayerActiveAlert(playerId, options = {}) {
  if (!playerId) return null;

  const response = await fetchAuth(`/players/${playerId}/active-alert`, options);
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to fetch player active alert");
  }

  return data.alert || null;
}

export async function upsertPlayerRiskAlert(playerId, payload = {}) {
  const response = await fetchAuth(`/players/${playerId}/risk-alert`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to save player risk alert");
  }

  return data.alert || null;
}

export async function clearPlayerRiskAlert(playerId) {
  const response = await fetchAuth(`/players/${playerId}/risk-alert`, { method: "DELETE" });
  const data = await readJsonSafe(response);

  if (!response.ok) {
    throw buildApiError(response, data, "Failed to clear player risk alert");
  }

  return data;
}
